import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Trash2, Shield, ShieldOff, Users, KeyRound, Settings2, Download, Upload, FolderUp, FileText, X, Loader2, CheckCircle2, Mail, Bell, AlertTriangle, Pencil, Calendar, Clock, Settings, Play, History, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_CATEGORIES = [
  { id: "fatture_estere", label: "Fatture Estere" },
  { id: "carta_credito", label: "Carta di Credito" },
  { id: "paghe", label: "Paghe" },
  { id: "estratti_conto", label: "Estratti Conto" },
  { id: "commissioni_pos", label: "Commissioni POS" },
  { id: "altre_ricevute", label: "Altre Ricevute" },
  { id: "riba_pagate", label: "RIBA Pagate" },
  { id: "riba_incassate", label: "RIBA Incassate" },
  { id: "assegni_pagati", label: "Assegni Pagati" },
  { id: "varie", label: "Varie" },
  { id: "inventario", label: "Inventario", monthNote: "Solo Gennaio" },
  { id: "dichiarazione_redditi", label: "Dichiarazione dei Redditi", monthNote: "Solo Aprile" },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [categoriesDialog, setCategoriesDialog] = useState({ open: false, user: null });
  const [uploadDialog, setUploadDialog] = useState({ open: false, user: null });
  const [editNameDialog, setEditNameDialog] = useState({ open: false, user: null });
  const [newUserName, setNewUserName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [savingCategories, setSavingCategories] = useState(false);
  
  // Upload state
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const currentYear = new Date().getFullYear();
  const validYears = [currentYear - 1, currentYear];
  
  // Reminders state
  const [sendingReminders, setSendingReminders] = useState(false);
  const [remindersPreview, setRemindersPreview] = useState(null);
  const [showRemindersDialog, setShowRemindersDialog] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState(new Set());
  const [excludedCategories, setExcludedCategories] = useState(new Set());
  const [showConfirmSendDialog, setShowConfirmSendDialog] = useState(false);
  
  // Scheduler settings state
  const [showSchedulerDialog, setShowSchedulerDialog] = useState(false);
  const [schedulerSettings, setSchedulerSettings] = useState(null);
  const [schedulerJobs, setSchedulerJobs] = useState([]);
  const [savingScheduler, setSavingScheduler] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState(null);
  
  // New client dialog state
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchSchedulerSettings();
  }, []);

  const fetchSchedulerSettings = async () => {
    try {
      const [settingsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/admin/scheduler/settings`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/scheduler/jobs`, { headers: getAuthHeader() })
      ]);
      setSchedulerSettings(settingsRes.data);
      setSchedulerJobs(jobsRes.data.jobs || []);
    } catch (error) {
      console.error("Error fetching scheduler settings:", error);
    }
  };

  const handleSaveSchedulerSettings = async () => {
    setSavingScheduler(true);
    try {
      await axios.put(`${API}/admin/scheduler/settings`, schedulerSettings, {
        headers: getAuthHeader()
      });
      toast.success("Impostazioni scheduler salvate");
      fetchSchedulerSettings();
      setShowSchedulerDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingScheduler(false);
    }
  };

  const handleTriggerJob = async (jobId) => {
    setTriggeringJob(jobId);
    try {
      const response = await axios.post(`${API}/admin/scheduler/trigger/${jobId}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'avvio del job");
    } finally {
      setTriggeringJob(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento degli utenti");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim() || !newClientEmail.trim()) {
      toast.error("Inserisci nome ed email");
      return;
    }
    
    setCreatingClient(true);
    try {
      const response = await axios.post(`${API}/admin/clients`, {
        name: newClientName.trim(),
        email: newClientEmail.trim().toLowerCase()
      }, {
        headers: getAuthHeader()
      });
      
      if (response.data.welcome_email_sent) {
        toast.success(`Cliente ${response.data.name} creato! Email di benvenuto inviata.`);
      } else {
        toast.success(`Cliente ${response.data.name} creato! (Email non inviata - configurazione mancante)`);
      }
      
      setShowNewClientDialog(false);
      setNewClientName("");
      setNewClientEmail("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione del cliente");
    } finally {
      setCreatingClient(false);
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, 
        { is_admin: !currentStatus },
        { headers: getAuthHeader() }
      );
      toast.success(currentStatus ? "Privilegi admin rimossi" : "Privilegi admin assegnati");
      fetchUsers();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    
    try {
      await axios.delete(`${API}/admin/users/${deleteDialog.user.id}`, {
        headers: getAuthHeader()
      });
      toast.success("Utente eliminato");
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog.user || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setResetting(true);
    try {
      await axios.put(
        `${API}/admin/users/${resetDialog.user.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success(`Password reimpostata per ${resetDialog.user.email}`);
      setResetDialog({ open: false, user: null });
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset password");
    } finally {
      setResetting(false);
    }
  };

  const openCategoriesDialog = (u) => {
    const enabled = u.enabled_categories || ALL_CATEGORIES.map(c => c.id);
    setSelectedCategories(enabled);
    setCategoriesDialog({ open: true, user: u });
  };

  const handleSaveCategories = async () => {
    if (!categoriesDialog.user) return;
    
    setSavingCategories(true);
    try {
      await axios.put(
        `${API}/admin/users/${categoriesDialog.user.id}/categories`,
        selectedCategories,
        { headers: getAuthHeader() }
      );
      toast.success("Categorie aggiornate");
      setCategoriesDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingCategories(false);
    }
  };

  // Backup state
  const [backupTask, setBackupTask] = useState(null);
  const [backupPolling, setBackupPolling] = useState(false);
  const backupPollInterval = useRef(null);

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => 
      prev.includes(catId)
        ? prev.filter(c => c !== catId)
        : [...prev, catId]
    );
  };

  // Start async backup
  const handleStartBackup = async () => {
    try {
      toast.info("Avvio backup completo...");
      const response = await axios.post(`${API}/admin/backup/start`, {}, {
        headers: getAuthHeader()
      });
      
      setBackupTask({
        task_id: response.data.task_id,
        status: response.data.status,
        progress: 0
      });
      
      // Start polling for status
      startBackupPolling(response.data.task_id);
      toast.success("Backup avviato! Puoi continuare a lavorare.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'avvio del backup");
    }
  };

  // Poll backup status
  const startBackupPolling = (taskId) => {
    setBackupPolling(true);
    
    // Clear any existing interval
    if (backupPollInterval.current) {
      clearInterval(backupPollInterval.current);
    }
    
    backupPollInterval.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/admin/backup/status/${taskId}`, {
          headers: getAuthHeader()
        });
        
        setBackupTask({
          task_id: taskId,
          ...response.data
        });
        
        if (response.data.status === "completed") {
          clearInterval(backupPollInterval.current);
          setBackupPolling(false);
          toast.success(`Backup completato! ${response.data.files_added} file (${response.data.size_mb} MB)`);
        } else if (response.data.status === "error") {
          clearInterval(backupPollInterval.current);
          setBackupPolling(false);
          toast.error(`Errore backup: ${response.data.error}`);
        }
      } catch (error) {
        console.error("Error polling backup status:", error);
      }
    }, 2000); // Poll every 2 seconds
  };

  // Download completed backup
  const handleDownloadBackup = async (taskId) => {
    try {
      toast.info("Download backup in corso...");
      const response = await axios.get(`${API}/admin/backup/download/${taskId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Backup_Completo_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup scaricato!");
    } catch (error) {
      toast.error("Errore nel download del backup");
    }
  };

  // Download metadata backup (quick)
  const handleDownloadMetadataBackup = async () => {
    try {
      toast.info("Preparazione backup metadati...");
      const response = await axios.get(`${API}/admin/backup`, {
        headers: getAuthHeader()
      });
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup_metadati_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup metadati scaricato!");
    } catch (error) {
      toast.error("Errore nel download del backup");
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (backupPollInterval.current) {
        clearInterval(backupPollInterval.current);
      }
    };
  }, []);

  // Email Reminders functions
  const handlePreviewReminders = async () => {
    try {
      console.log("Fetching reminders preview...");
      const response = await axios.get(`${API}/admin/reminders-preview`, {
        headers: getAuthHeader(),
        timeout: 30000 // 30 second timeout
      });
      console.log("Got response:", response.data);
      setRemindersPreview(response.data);
      setExcludedUsers(new Set()); // Reset exclusions
      setExcludedCategories(new Set());
      setShowRemindersDialog(true);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast.error("Errore nel caricamento dell'anteprima: " + (error.message || "Timeout"));
    }
  };

  const toggleUserExclusion = (userId) => {
    setExcludedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleCategoryExclusion = (category) => {
    setExcludedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get filtered users count
  const getFilteredUsersCount = () => {
    if (!remindersPreview) return 0;
    return remindersPreview.preview.filter(u => !excludedUsers.has(u.user_id)).length;
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {
        excluded_users: Array.from(excludedUsers),
        excluded_categories: Array.from(excludedCategories),
        confirm: true  // Conferma esplicita
      }, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
      setShowConfirmSendDialog(false);
      setShowRemindersDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dei promemoria");
    } finally {
      setSendingReminders(false);
    }
  };

  // Show confirmation dialog before sending
  const handleRequestSendReminders = () => {
    setShowConfirmSendDialog(true);
  };

  const openUploadDialog = (u) => {
    setUploadDialog({ open: true, user: u });
    setUploadYear(currentYear);
    setUploadMonth(new Date().getMonth() + 1);
    setUploadCategory("");
    setUploadFiles([]);
  };

  const openEditNameDialog = (u) => {
    setEditNameDialog({ open: true, user: u });
    setNewUserName(u.name);
  };

  const handleSaveName = async () => {
    if (!newUserName.trim()) {
      toast.error("Il nome non può essere vuoto");
      return;
    }
    
    setSavingName(true);
    try {
      await axios.put(
        `${API}/admin/users/${editNameDialog.user.id}`,
        { name: newUserName.trim() },
        { headers: getAuthHeader() }
      );
      toast.success("Nome aggiornato con successo");
      setEditNameDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error("Errore nell'aggiornamento del nome");
    } finally {
      setSavingName(false);
    }
  };

  const handleUploadForUser = async () => {
    if (!uploadDialog.user || !uploadCategory || uploadFiles.length === 0) {
      toast.error("Seleziona categoria e file");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", uploadCategory);
        formData.append("year", uploadYear);
        formData.append("month", uploadMonth);

        try {
          await axios.post(
            `${API}/admin/users/${uploadDialog.user.id}/documents/upload`,
            formData,
            {
              headers: {
                ...getAuthHeader(),
                "Content-Type": "multipart/form-data"
              }
            }
          );
          successCount++;
        } catch (err) {
          errorCount++;
          console.error("Error uploading file:", file.name, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Caricati ${successCount} file per ${uploadDialog.user.name}`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} file non caricati`);
      }

      setUploadDialog({ open: false, user: null });
      setUploadFiles([]);
    } catch (error) {
      toast.error("Errore durante il caricamento");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-[#A1A1AA] hover:text-[#D4AF37] -ml-2 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-2xl font-serif text-[#EDEDED]">Gestione Utenti</h1>
                <p className="text-[#52525B] text-sm">Pannello di amministrazione</p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Backup Status/Progress */}
              {backupTask && (backupTask.status === "processing" || backupTask.status === "starting") && (
                <div className="flex items-center gap-3 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-md">
                  <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
                  <div className="text-sm">
                    <p className="text-[#EDEDED]">Backup in corso...</p>
                    <p className="text-[#A1A1AA] text-xs">
                      {backupTask.progress || 0}% - {backupTask.files_processed || 0}/{backupTask.total_files || '?'} file
                    </p>
                  </div>
                  <Progress value={backupTask.progress || 0} className="w-24 h-2" />
                </div>
              )}
              
              {/* Backup Completed - Download Ready */}
              {backupTask && backupTask.status === "completed" && (
                <Button
                  onClick={() => handleDownloadBackup(backupTask.task_id)}
                  className="bg-[#10B981] text-white hover:bg-[#059669]"
                  data-testid="download-ready-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Scarica Backup ({backupTask.size_mb} MB)
                </Button>
              )}
              
              {/* New Client Button */}
              <Button
                onClick={() => setShowNewClientDialog(true)}
                className="bg-[#10B981] text-white hover:bg-[#059669]"
                data-testid="new-client-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Nuovo Cliente
              </Button>
              
              {/* Scheduler Settings Button */}
              <Button
                onClick={() => setShowSchedulerDialog(true)}
                variant="outline"
                className="border-neutral-600 text-neutral-400 hover:bg-neutral-800 hover:text-white"
                data-testid="scheduler-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Automazioni
              </Button>
              
              {/* Start Backup Button */}
              <Button
                onClick={handleStartBackup}
                disabled={backupPolling}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black disabled:opacity-50"
                data-testid="backup-full-btn"
              >
                {backupPolling ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {backupPolling ? "Backup in corso..." : "Backup Completo"}
              </Button>
              
              {/* Send Reminders Button */}
              <Button
                onClick={handlePreviewReminders}
                variant="outline"
                className="border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2] hover:text-white"
                data-testid="send-reminders-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Invia Promemoria
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-neutral-800 rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Nome</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Email</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Ruolo</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Categorie</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Registrato</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow 
                    key={u.id} 
                    className="border-neutral-800 hover:bg-neutral-900/50"
                    data-testid={`user-row-${u.id}`}
                  >
                    <TableCell className="text-[#EDEDED] font-medium">{u.name}</TableCell>
                    <TableCell className="text-[#A1A1AA] font-mono text-sm">{u.email}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={u.is_admin ? "default" : "secondary"}
                        className={u.is_admin ? "bg-[#D4AF37] text-black" : "bg-neutral-800 text-neutral-400"}
                      >
                        {u.is_admin ? "Admin" : "Utente"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-[#A1A1AA] text-sm">
                        {u.enabled_categories ? u.enabled_categories.length : ALL_CATEGORIES.length}/{ALL_CATEGORIES.length}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#52525B] font-mono text-sm">
                      {formatDate(u.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {u.id !== user.id && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditNameDialog(u)}
                              className="text-[#A1A1AA] hover:text-[#D4AF37]"
                              title="Modifica Nome"
                              data-testid={`edit-name-${u.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openUploadDialog(u)}
                              className="text-[#A1A1AA] hover:text-[#10B981]"
                              title="Carica Documenti per questo utente"
                              data-testid={`upload-for-${u.id}`}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openCategoriesDialog(u)}
                              className="text-[#A1A1AA] hover:text-[#D4AF37]"
                              title="Gestisci Categorie"
                              data-testid={`categories-${u.id}`}
                            >
                              <Settings2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setResetDialog({ open: true, user: u });
                                setNewPassword("");
                              }}
                              className="text-[#A1A1AA] hover:text-[#D4AF37]"
                              title="Reset Password"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                              className="text-[#A1A1AA] hover:text-[#D4AF37]"
                              title={u.is_admin ? "Rimuovi admin" : "Rendi admin"}
                              data-testid={`toggle-admin-${u.id}`}
                            >
                              {u.is_admin ? (
                                <ShieldOff className="w-4 h-4" />
                              ) : (
                                <Shield className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteDialog({ open: true, user: u })}
                              className="text-[#A1A1AA] hover:text-red-500"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {u.id === user.id && (
                          <span className="text-xs text-[#52525B]">Tu</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0A0A0A] border border-neutral-800 p-4 rounded-md">
            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Totale Utenti</p>
            <p className="text-2xl font-mono text-[#EDEDED]">{users.length}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-neutral-800 p-4 rounded-md">
            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Amministratori</p>
            <p className="text-2xl font-mono text-[#D4AF37]">{users.filter(u => u.is_admin).length}</p>
          </div>
        </div>
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: deleteDialog.user })}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED]">Conferma eliminazione</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Sei sicuro di voler eliminare l'utente <strong>{deleteDialog.user?.name}</strong>? 
              Questa azione eliminerà anche tutti i suoi documenti e non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialog({ open: false, user: null })}
              className="text-[#A1A1AA]"
              data-testid="cancel-delete-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Name Dialog */}
      <Dialog open={editNameDialog.open} onOpenChange={(open) => {
        setEditNameDialog({ open, user: editNameDialog.user });
        if (!open) setNewUserName("");
      }}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#D4AF37]" />
              Modifica Nome
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Modifica il nome per <strong>{editNameDialog.user?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Nome / Ragione Sociale</Label>
            <Input
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              placeholder="Nome o ragione sociale"
              className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
              data-testid="edit-name-input"
            />
            <p className="text-[#52525B] text-xs mt-2">
              Puoi inserire il nome della società invece del nome personale
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditNameDialog({ open: false, user: null })}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSaveName}
              disabled={savingName}
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
              data-testid="save-name-btn"
            >
              {savingName ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => {
        setResetDialog({ open, user: resetDialog.user });
        if (!open) setNewPassword("");
      }}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#D4AF37]" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Imposta una nuova password per <strong>{resetDialog.user?.name}</strong> ({resetDialog.user?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-password" className="text-[#A1A1AA] text-sm">
              Nuova Password
            </Label>
            <Input
              id="new-password"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimo 6 caratteri"
              className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
              data-testid="new-password-input"
            />
            <p className="text-xs text-[#52525B] mt-2">
              Comunica questa password al cliente in modo sicuro
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setResetDialog({ open: false, user: null });
                setNewPassword("");
              }}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetting || newPassword.length < 6}
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Reimposta Password"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories Dialog */}
      <Dialog open={categoriesDialog.open} onOpenChange={(open) => {
        setCategoriesDialog({ open, user: categoriesDialog.user });
      }}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#D4AF37]" />
              Categorie per {categoriesDialog.user?.name}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Seleziona le categorie di documenti che questo cliente deve caricare
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3 max-h-80 overflow-y-auto">
            {ALL_CATEGORIES.map((cat) => (
              <div 
                key={cat.id}
                className="flex items-center gap-3 p-2 rounded hover:bg-neutral-900"
              >
                <Checkbox
                  id={cat.id}
                  checked={selectedCategories.includes(cat.id)}
                  onCheckedChange={() => toggleCategory(cat.id)}
                  className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                />
                <label 
                  htmlFor={cat.id}
                  className="text-[#EDEDED] text-sm cursor-pointer flex-1"
                >
                  {cat.label}
                  {cat.monthNote && (
                    <span className="text-[#D4AF37] text-xs ml-2">({cat.monthNote})</span>
                  )}
                </label>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-between border-t border-neutral-800 pt-4">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCategories(ALL_CATEGORIES.map(c => c.id))}
                className="text-[#A1A1AA] text-xs"
              >
                Seleziona tutte
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCategories([])}
                className="text-[#A1A1AA] text-xs"
              >
                Deseleziona tutte
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCategoriesDialog({ open: false, user: null })}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSaveCategories}
              disabled={savingCategories || selectedCategories.length === 0}
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
              data-testid="save-categories-btn"
            >
              {savingCategories ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Salva"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload for User Dialog */}
      <Dialog open={uploadDialog.open} onOpenChange={(open) => {
        setUploadDialog({ open, user: uploadDialog.user });
        if (!open) setUploadFiles([]);
      }}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#10B981]" />
              Carica Documenti per {uploadDialog.user?.name}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Carica documenti per conto di questo cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Year & Month selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA] text-sm">Anno</Label>
                <Select value={String(uploadYear)} onValueChange={(v) => setUploadYear(Number(v))}>
                  <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-700">
                    {validYears.map(y => (
                      <SelectItem key={y} value={String(y)} className="text-[#EDEDED]">{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A1A1AA] text-sm">Mese</Label>
                <Select value={String(uploadMonth)} onValueChange={(v) => setUploadMonth(Number(v))}>
                  <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-700">
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i+1} value={String(i+1)} className="text-[#EDEDED]">{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Category selection */}
            <div>
              <Label className="text-[#A1A1AA] text-sm">Categoria</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                  <SelectValue placeholder="Seleziona categoria..." />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-700">
                  {ALL_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className="text-[#EDEDED]">{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File upload */}
            <div>
              <Label className="text-[#A1A1AA] text-sm">File</Label>
              <div className="mt-2">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-700 rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FolderUp className="w-8 h-8 text-[#A1A1AA] mb-2" />
                    <p className="text-sm text-[#A1A1AA]">
                      Clicca per selezionare file
                    </p>
                    <p className="text-xs text-[#52525B] mt-1">
                      PDF, Excel, Word, JPG (multipli)
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.xlsx,.xls,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                  />
                </label>
              </div>

              {/* Selected files list */}
              {uploadFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-[#A1A1AA]">{uploadFiles.length} file selezionati:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {uploadFiles.map((file, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-[#EDEDED] bg-neutral-800 p-2 rounded">
                        <FileText className="w-3 h-3 text-[#D4AF37]" />
                        <span className="truncate flex-1">{file.name}</span>
                        <button 
                          onClick={() => setUploadFiles(uploadFiles.filter((_, idx) => idx !== i))}
                          className="text-[#A1A1AA] hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setUploadDialog({ open: false, user: null });
                setUploadFiles([]);
              }}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleUploadForUser}
              disabled={uploading || !uploadCategory || uploadFiles.length === 0}
              className="bg-[#10B981] text-white hover:bg-[#059669]"
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Carica {uploadFiles.length} file
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminders Preview Dialog */}
      <Dialog open={showRemindersDialog} onOpenChange={setShowRemindersDialog}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#1877F2]" />
              Anteprima Promemoria Email
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Seleziona quali clienti e categorie includere nel promemoria
            </DialogDescription>
          </DialogHeader>

          {remindersPreview ? (
            <div className="py-4 space-y-6">
              {/* Stats */}
              <div className="flex items-center gap-4 p-4 bg-neutral-900 rounded-lg">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#D4AF37]">{getFilteredUsersCount()}</p>
                  <p className="text-xs text-[#A1A1AA]">Utenti selezionati</p>
                </div>
                <div className="h-12 w-px bg-neutral-700" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#EDEDED]">{remindersPreview.users_with_missing_docs}</p>
                  <p className="text-xs text-[#A1A1AA]">Con doc. mancanti</p>
                </div>
                <div className="h-12 w-px bg-neutral-700" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#52525B]">{excludedUsers.size}</p>
                  <p className="text-xs text-[#A1A1AA]">Esclusi</p>
                </div>
              </div>

              {/* Categories to exclude */}
              <div>
                <p className="text-[#EDEDED] text-sm font-medium mb-3">Escludi categorie dal controllo:</p>
                <div className="flex flex-wrap gap-2">
                  {ALL_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategoryExclusion(cat.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        excludedCategories.has(cat.id)
                          ? "bg-red-500/20 text-red-400 border border-red-500/50"
                          : "bg-neutral-800 text-[#A1A1AA] border border-neutral-700 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      {excludedCategories.has(cat.id) ? "✕ " : ""}{cat.label}
                    </button>
                  ))}
                </div>
                {excludedCategories.size > 0 && (
                  <p className="text-xs text-[#52525B] mt-2">
                    Le categorie escluse non verranno considerate mancanti
                  </p>
                )}
              </div>

              {/* Users list */}
              {remindersPreview.preview.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[#EDEDED] text-sm font-medium">Clienti con documenti mancanti:</p>
                    <button
                      onClick={() => {
                        if (excludedUsers.size === remindersPreview.preview.length) {
                          setExcludedUsers(new Set());
                        } else {
                          setExcludedUsers(new Set(remindersPreview.preview.map(u => u.user_id)));
                        }
                      }}
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      {excludedUsers.size === remindersPreview.preview.length ? "Seleziona tutti" : "Deseleziona tutti"}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {remindersPreview.preview.map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                          excludedUsers.has(item.user_id)
                            ? "bg-neutral-950 border-neutral-800 opacity-50"
                            : "bg-neutral-900 border-neutral-800 hover:border-[#D4AF37]/30"
                        }`}
                        onClick={() => toggleUserExclusion(item.user_id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={!excludedUsers.has(item.user_id)}
                            className="border-neutral-600 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-[#EDEDED] font-medium">{item.name}</p>
                                <p className="text-[#52525B] text-xs">{item.email}</p>
                              </div>
                              <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-0">
                                {item.total_missing_categories} cat.
                              </Badge>
                            </div>
                            <div className="text-xs text-[#A1A1AA] mt-1">
                              {item.details.slice(0, 2).map((d, i) => (
                                <span key={i} className="mr-2">
                                  {d.month_name}: {d.missing_category_names.slice(0, 2).join(", ")}
                                  {d.missing_category_names.length > 2 && "..."}
                                </span>
                              ))}
                              {item.details.length > 2 && <span className="text-[#52525B]">+{item.details.length - 2} mesi</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
                  <p className="text-[#EDEDED]">Tutti gli utenti sono in regola!</p>
                  <p className="text-[#A1A1AA] text-sm">Nessun documento mancante da segnalare.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowRemindersDialog(false)}
              className="text-[#A1A1AA]"
            >
              Chiudi
            </Button>
            {remindersPreview && getFilteredUsersCount() > 0 && (
              <Button
                onClick={handleRequestSendReminders}
                className="bg-[#1877F2] text-white hover:bg-[#166FE5]"
                data-testid="request-send-reminders-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Invia a {getFilteredUsersCount()} clienti
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmSendDialog} onOpenChange={setShowConfirmSendDialog}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
              Conferma Invio Email
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Stai per inviare un'email di promemoria
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-4 mb-4">
              <p className="text-[#F59E0B] font-medium text-center text-lg">
                {getFilteredUsersCount()} email verranno inviate
              </p>
            </div>
            <p className="text-[#A1A1AA] text-sm text-center">
              Questa azione invierà un'email di promemoria a tutti i clienti selezionati. 
              Sei sicura di voler procedere?
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowConfirmSendDialog(false)}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSendReminders}
              disabled={sendingReminders}
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
              data-testid="confirm-send-reminders-btn"
            >
              {sendingReminders ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              {sendingReminders ? "Invio in corso..." : "Sì, Invia Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Client Dialog */}
      <Dialog open={showNewClientDialog} onOpenChange={(open) => {
        setShowNewClientDialog(open);
        if (!open) {
          setNewClientName("");
          setNewClientEmail("");
        }
      }}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#10B981]" />
              Nuovo Cliente
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Crea un account per un nuovo cliente. Riceverà un'email con le istruzioni per accedere.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Nome / Ragione Sociale</Label>
              <Input
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Es. Mario Rossi o Rossi Srl"
                className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
                data-testid="new-client-name"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={newClientEmail}
                onChange={(e) => setNewClientEmail(e.target.value)}
                placeholder="cliente@email.com"
                className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
                data-testid="new-client-email"
              />
            </div>
            
            <div className="bg-neutral-900 p-3 rounded-lg">
              <p className="text-[#A1A1AA] text-xs">
                <strong className="text-[#D4AF37]">Cosa succede dopo?</strong><br/>
                Il cliente riceverà un'email con un link per impostare la sua password e accedere alla piattaforma.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowNewClientDialog(false)}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleCreateClient}
              disabled={creatingClient || !newClientName.trim() || !newClientEmail.trim()}
              className="bg-[#10B981] text-white hover:bg-[#059669]"
              data-testid="create-client-btn"
            >
              {creatingClient ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              {creatingClient ? "Creazione..." : "Crea Cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scheduler Settings Dialog */}
      <Dialog open={showSchedulerDialog} onOpenChange={setShowSchedulerDialog}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#D4AF37]" />
              Automazioni Schedulatore
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Configura l'invio automatico di promemoria e backup
            </DialogDescription>
          </DialogHeader>

          {schedulerSettings ? (
            <div className="py-4 space-y-6">
              {/* Scheduled Jobs Status */}
              <div className="bg-neutral-900 rounded-lg p-4">
                <h3 className="text-[#EDEDED] font-medium mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  Job Programmati
                </h3>
                <div className="space-y-2">
                  {schedulerJobs.map((job) => (
                    <div key={job.id} className="flex items-center justify-between p-3 bg-neutral-800 rounded">
                      <div>
                        <p className="text-[#EDEDED] text-sm font-medium">
                          {job.id === "monthly_reminders" ? "Promemoria Email" : "Backup Automatico"}
                        </p>
                        <p className="text-[#52525B] text-xs">
                          Prossima esecuzione: {job.next_run ? new Date(job.next_run).toLocaleString("it-IT") : "Non programmato"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerJob(job.id)}
                        disabled={triggeringJob === job.id}
                        className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                      >
                        {triggeringJob === job.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reminders Settings */}
              <div className="border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#EDEDED] font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#1877F2]" />
                    Promemoria Automatici
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={schedulerSettings.reminders_enabled}
                      onCheckedChange={(checked) => 
                        setSchedulerSettings({...schedulerSettings, reminders_enabled: checked})
                      }
                      className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                    />
                    <span className="text-[#A1A1AA] text-sm">Attivo</span>
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Giorno del Mese</Label>
                    <Select
                      value={String(schedulerSettings.reminders_day)}
                      onValueChange={(v) => setSchedulerSettings({...schedulerSettings, reminders_day: Number(v)})}
                    >
                      <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-700 max-h-48">
                        {[...Array(28)].map((_, i) => (
                          <SelectItem key={i+1} value={String(i+1)} className="text-[#EDEDED]">
                            {i+1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Ora</Label>
                    <Select
                      value={String(schedulerSettings.reminders_hour)}
                      onValueChange={(v) => setSchedulerSettings({...schedulerSettings, reminders_hour: Number(v)})}
                    >
                      <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-700 max-h-48">
                        {[...Array(24)].map((_, i) => (
                          <SelectItem key={i} value={String(i)} className="text-[#EDEDED]">
                            {String(i).padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Mesi da Controllare</Label>
                    <Select
                      value={String(schedulerSettings.reminders_check_months)}
                      onValueChange={(v) => setSchedulerSettings({...schedulerSettings, reminders_check_months: Number(v)})}
                    >
                      <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-700">
                        <SelectItem value="1" className="text-[#EDEDED]">Ultimo mese</SelectItem>
                        <SelectItem value="2" className="text-[#EDEDED]">Ultimi 2 mesi</SelectItem>
                        <SelectItem value="3" className="text-[#EDEDED]">Ultimi 3 mesi</SelectItem>
                        <SelectItem value="6" className="text-[#EDEDED]">Ultimi 6 mesi</SelectItem>
                        <SelectItem value="12" className="text-[#EDEDED]">Ultimo anno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {schedulerSettings.last_reminder_sent && (
                  <p className="text-[#52525B] text-xs mt-3">
                    Ultimo invio: {new Date(schedulerSettings.last_reminder_sent).toLocaleString("it-IT")}
                    {schedulerSettings.last_reminder_count && ` (${schedulerSettings.last_reminder_count} email)`}
                  </p>
                )}
              </div>

              {/* Backup Settings */}
              <div className="border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#EDEDED] font-medium flex items-center gap-2">
                    <Download className="w-4 h-4 text-[#10B981]" />
                    Backup Automatici
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={schedulerSettings.backup_enabled}
                      onCheckedChange={(checked) => 
                        setSchedulerSettings({...schedulerSettings, backup_enabled: checked})
                      }
                      className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                    />
                    <span className="text-[#A1A1AA] text-sm">Attivo</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Giorno del Mese</Label>
                    <Select
                      value={String(schedulerSettings.backup_day)}
                      onValueChange={(v) => setSchedulerSettings({...schedulerSettings, backup_day: Number(v)})}
                    >
                      <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-700 max-h-48">
                        {[...Array(28)].map((_, i) => (
                          <SelectItem key={i+1} value={String(i+1)} className="text-[#EDEDED]">
                            {i+1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Ora</Label>
                    <Select
                      value={String(schedulerSettings.backup_hour)}
                      onValueChange={(v) => setSchedulerSettings({...schedulerSettings, backup_hour: Number(v)})}
                    >
                      <SelectTrigger className="mt-1 bg-neutral-900 border-neutral-700 text-[#EDEDED]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-neutral-700 max-h-48">
                        {[...Array(24)].map((_, i) => (
                          <SelectItem key={i} value={String(i)} className="text-[#EDEDED]">
                            {String(i).padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {schedulerSettings.last_backup_completed && (
                  <p className="text-[#52525B] text-xs mt-3">
                    Ultimo backup: {new Date(schedulerSettings.last_backup_completed).toLocaleString("it-IT")}
                    {schedulerSettings.last_backup_status && ` (${schedulerSettings.last_backup_status})`}
                  </p>
                )}
              </div>

              <p className="text-[#52525B] text-xs">
                I job vengono eseguiti automaticamente secondo la programmazione. Il fuso orario è Europe/Rome.
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowSchedulerDialog(false)}
              className="text-[#A1A1AA]"
            >
              Chiudi
            </Button>
            <Button
              onClick={handleSaveSchedulerSettings}
              disabled={savingScheduler}
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
              data-testid="save-scheduler-btn"
            >
              {savingScheduler ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Salva Impostazioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
